import styled from '@emotion/styled';
import { DateRangeInput } from '@eventbrite/event-create';
import { gettext, ngettext } from '@eventbrite/i18n';
import { getFormValidationAdornment, Typography } from '@eventbrite/marmalade';
import { format, isAfter, isSameYear, isValid, set } from 'date-fns';
import React, { useCallback } from 'react';
import {
    Controller,
    ControllerRenderProps,
    useFormContext,
} from 'react-hook-form';
import { useMedia } from 'react-use';
import { AutoCreateEventFormValues } from '../types';
import { TimeInput } from './Timepicker';

export enum DateFormFieldTestIds {
    Date = 'AutoCreateEventDateFormField',
    StartTime = 'AutoCreateEventStartTimeFormField',
    EndTime = 'AutoCreateEventEndTimeFormField',
}

type DateField = ControllerRenderProps<AutoCreateEventFormValues, 'dates'>;
type StartTimeField = ControllerRenderProps<
    AutoCreateEventFormValues,
    'startTime'
>;
type EndTimeField = ControllerRenderProps<AutoCreateEventFormValues, 'endTime'>;

type Field = ControllerRenderProps<
    AutoCreateEventFormValues,
    'dates' | 'startTime' | 'endTime'
>;
type Callback = () => void;

function onChangeAdapter<F extends Field, V>({
    field,
    callback,
    validate,
}: {
    field: F;
    callback?: Callback;
    validate?: Callback;
}) {
    return (value: V) => {
        field.onChange({
            target: { value },
        });

        if (validate) {
            validate();
        }

        if (callback) {
            setTimeout(callback, 100);
        }
    };
}

const reactHookFormAdapter = <F extends Field, V>({
    field,
    defaultValue,
    validate,
    callback,
}: {
    field: F;
    defaultValue: V;
    validate?: Callback;
    callback?: Callback;
}) => ({
    ...field,
    value: field.value || defaultValue,
    onBlur: () => {
        field.onBlur();
        if (validate) {
            validate();
        }
    },
    onChange: onChangeAdapter<F, V>({ field, validate, callback }),
});

const getUniqueDates = (dates: Date[]) =>
    dates.filter(
        (date, i, self) =>
            self.findIndex((d) => d.getTime() === date.getTime()) === i,
    );

const getTextHelperForField = (
    field: 'startTime' | 'endTime',
    dates: Date[],
) => {
    const DEFAULT_FORMAT = 'LLLL dd';
    const FORMAT_WITH_YEAR = 'LLLL dd, yyyy';
    if (!dates || dates.length === 0) {
        return '';
    }

    const uniqueDates = getUniqueDates(dates);
    if (uniqueDates.length === 1) {
        return '';
    }

    const [start, end] = dates;

    const haveSameYear = isSameYear(start, end);
    const dateFormat = haveSameYear ? DEFAULT_FORMAT : FORMAT_WITH_YEAR;

    return field === 'startTime'
        ? format(start, dateFormat)
        : format(end, dateFormat);
};

export function getTime(timeString?: string) {
    if (!timeString) {
        return { h: 0, m: 0 };
    }

    const match = timeString.match(/(\d{1,2}):(\d{2})(?:\s?(AM|PM))?/i);
    if (!match) {
        throw new Error(`Invalid time: ${timeString}`);
    }

    let h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    const period = match[3];

    if (period) {
        if (h === 12) {
            // 12:00 AM is midnight, 12:00 PM is noon
            h = period.toUpperCase() === 'AM' ? 0 : 12;
        } else if (period.toUpperCase() === 'PM') {
            h += 12;
        }
    }

    return { h, m };
}

const timeWithOffset = (time: string, offset: number) => {
    const { h, m } = getTime(time);
    return {
        h,
        m: m + offset,
    };
};

const getEndTimeStartInterval = (dates: Date[], startTime: string) => {
    const DEFAULT_INTERVAL = 30;
    const DEFAULT_START_TIME = {
        h: 0,
        m: 0,
    };

    if (!dates || dates.length === 0) {
        return undefined;
    }

    const uniqueDates = getUniqueDates(dates);
    if (uniqueDates.length === 1) {
        return timeWithOffset(startTime, DEFAULT_INTERVAL);
    } else {
        return DEFAULT_START_TIME;
    }
};
export const DatesFormField = () => {
    const { control, setFocus, watch, getValues, trigger } =
        useFormContext<AutoCreateEventFormValues>();
    const dates = watch('dates');
    const startTime = watch('startTime');
    const isTouchDevice = useMedia('(hover: none) and (pointer: coarse)');

    const validateDates = (value: Date[]) => {
        const NOW = new Date();
        const { startTime, endTime } = getValues();
        const times = [startTime, endTime];

        const setTime = ({
            date,
            hours,
            minutes,
        }: {
            date: Date;
            hours: number;
            minutes: number;
        }) => set(date, { hours, minutes });

        const isValidDate = (date: Date) => isValid(date) && isAfter(date, NOW);

        if (!value || value.length === 0) {
            return gettext('Event dates are required').toString();
        }

        return value
            .map((date, index) => {
                const { h, m } = getTime(times[index]);
                return setTime({ date, hours: h, minutes: m });
            })
            .every(isValidDate)
            ? undefined
            : gettext('Invalid dates').toString();
    };

    const validateEndTime = (value: string) => {
        const { dates, startTime } = getValues();
        const uniqueDates = getUniqueDates(dates);
        const startTimeObject = getTime(startTime);
        const endTimeObject = getTime(value);

        if (uniqueDates.length === 1) {
            if (
                endTimeObject.h < startTimeObject.h ||
                (endTimeObject.h === startTimeObject.h &&
                    endTimeObject.m <= startTimeObject.m)
            ) {
                return gettext('End time must be after start time').toString();
            }
        }

        return undefined;
    };

    const focusNextField = useCallback(
        (field: 'startTime' | 'endTime') => () =>
            !isTouchDevice && setFocus(field),
        [setFocus, isTouchDevice],
    );

    return (
        <>
            <Title>
                <Typography as="h2" color="neutral-900" variant="body-lg">
                    {gettext('When does your event start and end?').toString()}
                </Typography>
            </Title>

            <Fields>
                <DateField>
                    <Controller
                        name="dates"
                        control={control}
                        rules={{
                            validate: {
                                validDates: validateDates,
                            },
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <DateRangeInput
                                hasError={!!error}
                                helperText={error && error.message}
                                id="auto-create-form-dates"
                                label={gettext('Date').toString()}
                                getActionLabel={(count: number) =>
                                    ngettext(
                                        'Select date',
                                        'Select dates',
                                        count,
                                    ).toString()
                                }
                                headerLabel={gettext(
                                    'When is your event?',
                                ).toString()}
                                validationAdornment={getFormValidationAdornment(
                                    {
                                        hasError: !!error,
                                    },
                                )}
                                {...field}
                                {...reactHookFormAdapter<DateField, Date[]>({
                                    field,
                                    defaultValue: [],
                                    validate: () => {
                                        trigger([
                                            'dates',
                                            'startTime',
                                            'endTime',
                                        ]);
                                    },
                                    callback: focusNextField('startTime'),
                                })}
                            />
                        )}
                    />
                </DateField>
                <TimeFields>
                    <Controller
                        name="startTime"
                        control={control}
                        rules={{
                            required: gettext(
                                'Start time is required',
                            ).toString(),
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <TimeInput
                                hasError={!!error}
                                helperText={
                                    error
                                        ? error.message
                                        : getTextHelperForField(
                                              'startTime',
                                              dates,
                                          )
                                }
                                id="auto-create-form-start-time"
                                label={gettext('Start time').toString()}
                                validationAdornment={getFormValidationAdornment(
                                    {
                                        hasError: !!error,
                                    },
                                )}
                                {...field}
                                {...reactHookFormAdapter<
                                    StartTimeField,
                                    string
                                >({
                                    field,
                                    defaultValue: '',
                                    validate: () => {
                                        trigger([
                                            'dates',
                                            'startTime',
                                            'endTime',
                                        ]);
                                    },
                                    callback: focusNextField('endTime'),
                                })}
                            />
                        )}
                    />

                    <Controller
                        name="endTime"
                        control={control}
                        rules={{
                            required: gettext(
                                'End time is required',
                            ).toString(),
                            validate: {
                                validateEndTime,
                            },
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <TimeInput
                                hasError={!!error}
                                helperText={
                                    error
                                        ? error.message
                                        : getTextHelperForField(
                                              'endTime',
                                              dates,
                                          )
                                }
                                id="auto-create-form-end-time"
                                label={gettext('End time').toString()}
                                validationAdornment={getFormValidationAdornment(
                                    {
                                        hasError: !!error,
                                    },
                                )}
                                {...field}
                                {...reactHookFormAdapter<EndTimeField, string>({
                                    field,
                                    defaultValue: '',
                                    validate: () => {
                                        trigger([
                                            'dates',
                                            'startTime',
                                            'endTime',
                                        ]);
                                    },
                                })}
                                minTime={getEndTimeStartInterval(
                                    dates,
                                    startTime,
                                )}
                            />
                        )}
                    />
                </TimeFields>
            </Fields>
        </>
    );
};

const Title = styled.div`
    margin-bottom: 24px;
`;

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (min-width: 640px) {
        flex-direction: row;
    }
`;

const DateField = styled.div`
    @media (min-width: 640px) {
        flex: 1 1 50%;
    }
`;

const TimeFields = styled.div`
    display: flex;
    gap: 16px;
    width: 100%;

    > div {
        flex: 1 1 50%;
    }

    @media (min-width: 640px) {
        flex: 1 1 50%;
    }
`;
